import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Stroke } from '@frisk/shared';
import { api } from '../../api';
import { PATHS } from '../../navigation';
import { useStore } from '../../store';
import { Widget, WidgetEmpty } from './Widget';

/**
 * The family message board.
 *
 * A read-only miniature of the notes canvas — the point is seeing that someone
 * wrote something from across the kitchen, not reading it there. Tapping opens
 * the real board.
 */
export function NotesWidget() {
  const version = useStore((s) => s.notesVersion);
  const [strokes, setStrokes] = useState<Stroke[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    void api
      .getNotes()
      .then((n) => !cancelled && setStrokes(n.strokes))
      .catch(() => !cancelled && setStrokes([]));
    return () => {
      cancelled = true;
    };
    // notesVersion ticks whenever the board is saved anywhere, including on
    // another device — this is the same signal the notes screen listens to.
  }, [version]);

  // Board coordinates are already normalised to 1 = full width, with y running
  // past 1 on a tall canvas, so the preview only has to pick a viewBox.
  const height = strokes?.length
    ? Math.max(1, ...strokes.flatMap((s) => s.points.map((p) => p.y)))
    : 1;

  return (
    <Widget kicker="NOTIZEN" onTap={() => navigate(PATHS.notes)}>
      {strokes === null ? (
        <WidgetEmpty> </WidgetEmpty>
      ) : strokes.length === 0 ? (
        <WidgetEmpty>Nichts notiert.</WidgetEmpty>
      ) : (
        <div style={{ flex: 1, minHeight: 0, marginTop: 12, display: 'flex' }}>
          <svg
            viewBox={`0 0 1 ${height}`}
            preserveAspectRatio="xMidYMin meet"
            style={{ width: '100%', height: '100%', overflow: 'hidden' }}
          >
            {strokes.map((s, i) => (
              <polyline
                key={i}
                points={s.points.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={s.color}
                strokeLinecap="round"
                strokeLinejoin="round"
                // With non-scaling-stroke the width is in screen pixels, not
                // board units — so the board's own width (~0.004) would render
                // as a hairline far below one pixel and vanish. Fixed pixel
                // widths are what a shrunken preview wants anyway: they keep
                // the handwriting readable as a shape at any tile size.
                vectorEffect="non-scaling-stroke"
                strokeWidth={s.width > 0.01 ? 3 : 1.75}
                strokeOpacity={0.92}
              />
            ))}
          </svg>
        </div>
      )}
    </Widget>
  );
}
